import React, { useState, useEffect, useContext } from 'react';
import { AppContext } from '../context/AppContext';
import { useTranslation } from 'react-i18next';
import DataTable from '../components/DataTable';
import Modal from '../components/Modal';

const DirectReturn = () => {
    const { authFetch, selectedDepo } = useContext(AppContext);
    const { t } = useTranslation();
    
    const [soldItems, setSoldItems] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    
    // Date Filter State
    const [dateFilter, setDateFilter] = useState(new Date().toISOString().split('T')[0]);
    
    // Modal State
    const [isReturnModalOpen, setIsReturnModalOpen] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);
    const [returnQty, setReturnQty] = useState(1);
    const [returnReason, setReturnReason] = useState('');
    const [isProcessing, setIsProcessing] = useState(false);

    const fetchSoldItems = () => {
        setIsLoading(true);
        // Add cache buster timestamp
        const tStamp = Date.now();
        authFetch(`/api/direct_returns?depo_id=${selectedDepo}&date=${dateFilter}&_t=${tStamp}`)
            .then(r => r.json())
            .then(d => {
                if (Array.isArray(d)) {
                    setSoldItems(d);
                } else {
                    console.error('API Error or invalid format:', d);
                    alert('API returned an invalid format: ' + (d.error || JSON.stringify(d)));
                }
            })
            .catch(e => {
                console.error('Network Error:', e);
                alert('Network Error: ' + e.message);
            })
            .finally(() => setIsLoading(false));
    };

    useEffect(() => {
        fetchSoldItems();
    }, [authFetch, selectedDepo, dateFilter]);

    const handleOpenReturnModal = (item) => {
        setSelectedItem(item);
        setReturnQty(1);
        setReturnReason('');
        setIsReturnModalOpen(true);
    };

    const handleConfirmReturn = async (e) => {
        e.preventDefault();
        
        if (!selectedItem) return;
        if (returnQty <= 0 || returnQty > parseFloat(selectedItem.sold_qty)) {
            alert(t('Invalid return quantity.'));
            return;
        }
        if (!returnReason.trim()) {
            alert(t('Please provide a reason for the return.'));
            return;
        }

        setIsProcessing(true);
        try {
            const res = await authFetch('/api/refunds', {
                method: 'POST',
                body: JSON.stringify({
                    invoice_id: selectedItem.invoice_id,
                    type: 'partial',
                    items: [{
                        invoice_item_id: selectedItem.invoice_item_id,
                        qty_refunded: returnQty
                    }],
                    reason: returnReason
                })
            });
            const data = await res.json();
            
            if (res.ok) {
                alert(t('Return processed successfully!'));
                setIsReturnModalOpen(false);
                fetchSoldItems(); // Refresh the list
            } else {
                alert(data.message || t('Return failed.'));
            }
        } catch (err) {
            console.error(err);
            alert(t('Network error while processing return.'));
        } finally {
            setIsProcessing(false);
        }
    };

    const columns = [
        { 
            header: t('Time'), 
            accessor: 'created_at', 
            render: r => new Date(r.created_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) 
        },
        { 
            header: t('Invoice No'), 
            accessor: 'invoice_number',
            render: r => <span className="font-mono text-gray-500 font-bold">{r.invoice_number}</span> 
        },
        { 
            header: t('Product'), 
            accessor: 'product_name',
            render: r => (
                <div>
                    <div className="font-bold text-gray-800">{r.product_name}</div>
                    <div className="text-xs text-gray-500">{r.barcode || r.sku}</div>
                </div>
            )
        },
        { 
            header: t('Customer'), 
            accessor: 'customer_name',
            render: r => r.customer_name || t('Walk-in')
        },
        { 
            header: t('Qty Sold'), 
            accessor: 'sold_qty',
            render: r => <span className="font-bold">{parseFloat(r.sold_qty)}</span>
        },
        { 
            header: t('Total Price'), 
            accessor: 'price_ttc',
            render: r => <span className="font-semibold text-emerald-600">CFA{parseFloat(r.price_ttc).toFixed(2)}</span>
        },
        { 
            header: t('Action'), 
            accessor: 'id', 
            render: r => (
                <button 
                    onClick={() => handleOpenReturnModal(r)}
                    className="flex items-center gap-1 bg-red-50 text-red-600 hover:bg-red-100 hover:text-red-700 px-3 py-1.5 rounded-md text-sm font-bold transition-colors"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    {t('Return')}
                </button>
            )
        }
    ];

    return (
        <div className="space-y-6">
            <header className="flex flex-col md:flex-row justify-between items-start md:items-end gap-4">
                <div>
                    <h2 className="text-2xl font-bold text-gray-800">{t('Direct Product Return')}</h2>
                    <p className="text-gray-500 mt-1">
                        {dateFilter 
                            ? <>{t('Select any product sold on')} <span className="font-bold text-gray-700">{dateFilter}</span> {t('to quickly process a return.')}</>
                            : t('Showing latest products sold. Clear date filter to see all recent sales.')
                        }
                    </p>
                </div>
                <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2">
                        <label className="text-sm font-semibold text-gray-600">{t('Date:')}</label>
                        <div className="relative flex items-center">
                            <input 
                                type="date"
                                value={dateFilter}
                                onChange={(e) => setDateFilter(e.target.value)}
                                className="border p-2 rounded-lg focus:ring-2 focus:ring-indigo-500 text-sm font-medium pr-8"
                            />
                            {dateFilter && (
                                <button 
                                    onClick={() => setDateFilter('')}
                                    className="absolute right-2 text-gray-400 hover:text-red-500 bg-white"
                                    title="Clear date"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            )}
                        </div>
                    </div>
                    <button 
                        onClick={fetchSoldItems}
                        className="flex items-center gap-2 text-indigo-600 bg-indigo-50 hover:bg-indigo-100 px-4 py-2 rounded-lg font-bold transition-colors"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" className={`h-5 w-5 ${isLoading ? 'animate-spin' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                        {t('Refresh List')}
                    </button>
                </div>
            </header>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200">
                <DataTable columns={columns} data={soldItems} />
            </div>

            <Modal isOpen={isReturnModalOpen} onClose={() => !isProcessing && setIsReturnModalOpen(false)} title={t('Return Product')} size="max-w-lg">
                {selectedItem && (
                    <form onSubmit={handleConfirmReturn} className="space-y-5">
                        <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                            <div className="flex justify-between items-start mb-3 border-b border-gray-200 pb-3">
                                <div>
                                    <div className="text-xs text-gray-500 uppercase font-bold tracking-wider mb-1">{t('Receipt No')}</div>
                                    <div className="font-mono font-bold text-lg text-gray-800">{selectedItem.invoice_number}</div>
                                </div>
                                <div className="text-right">
                                    <div className="text-xs text-gray-500 uppercase font-bold tracking-wider mb-1">{t('Customer')}</div>
                                    <div className="font-bold text-gray-700">{selectedItem.customer_name || t('Walk-in')}</div>
                                </div>
                            </div>
                            
                            <div>
                                <div className="text-xs text-gray-500 uppercase font-bold tracking-wider mb-1">{t('Product Details')}</div>
                                <div className="font-bold text-gray-800 text-lg">{selectedItem.product_name}</div>
                                <div className="flex justify-between items-center mt-2 text-sm">
                                    <span className="text-gray-600 font-medium">{t('Sold Qty:')} {parseFloat(selectedItem.sold_qty)}</span>
                                    <span className="text-emerald-600 font-bold">CFA{parseFloat(selectedItem.price_ttc).toFixed(2)}</span>
                                </div>
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-bold text-gray-700 mb-1">{t('Quantity to Return')}</label>
                            <input 
                                type="number" 
                                min="0.01" 
                                max={parseFloat(selectedItem.sold_qty)}
                                step="0.01"
                                className="w-full border p-3 rounded-lg focus:ring-2 focus:ring-indigo-500 font-bold text-lg" 
                                value={returnQty}
                                onChange={e => setReturnQty(parseFloat(e.target.value) || '')}
                                required
                            />
                            <p className="text-xs text-gray-500 mt-1">Maximum allowed: {parseFloat(selectedItem.sold_qty)}</p>
                        </div>

                        <div>
                            <label className="block text-sm font-bold text-gray-700 mb-1">{t('Reason for Return')}</label>
                            <textarea 
                                required
                                className="w-full border p-3 rounded-lg focus:ring-2 focus:ring-indigo-500" 
                                rows="3"
                                value={returnReason}
                                onChange={e => setReturnReason(e.target.value)}
                                placeholder={t('e.g. Defective product, customer changed mind...')}
                            ></textarea>
                        </div>

                        <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                            <button 
                                type="button" 
                                onClick={() => setIsReturnModalOpen(false)} 
                                disabled={isProcessing}
                                className="px-5 py-2.5 text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg font-bold transition-colors disabled:opacity-50"
                            >
                                {t('Cancel')}
                            </button>
                            <button 
                                type="submit" 
                                disabled={isProcessing}
                                className="px-5 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 font-bold shadow transition-colors flex items-center gap-2 disabled:opacity-50"
                            >
                                {isProcessing && (
                                    <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                )}
                                {t('Confirm Return')}
                            </button>
                        </div>
                    </form>
                )}
            </Modal>
        </div>
    );
};

export default DirectReturn;
